import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FaEnvelope, FaLock } from "react-icons/fa";
import { Spinner } from "react-bootstrap";
import ForgotPasswordModal from "../../components/ForgotPasswordModal/ForgotPasswordModal";
import BlockedUser from "../../components/BlockedUser/BlockedUser";
import FiemgLogo from "../../assets/Fiemg.svg";

import {
  loginInit,
  loginLocal,
  loginSso,
  getPerfil,
} from "../../services/authService";
import { getMsalInstance } from "../../services/msalConfig";
import { emitAuthChanged, saveTokens } from "../../services/authSession";
import { PATHS } from "../../routes/paths";

import "./Login.css";
import "bootstrap/dist/css/bootstrap.min.css";

const PRIVACY_URL = "https://www.fiemg.com.br/privacidade-e-protecao-de-dados/";


const SSO_CFG_KEY = "sso_cfg";
const SSO_EMAIL_KEY = "sso_email";
const SSO_PENDING_KEY = "sso_pending";

type InputFieldProps = {
  type: string;
  placeholder: string;
  icon: React.ComponentType<any>;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
};

const InputField = ({
  type,
  placeholder,
  icon: Icon,
  value,
  onChange,
}: InputFieldProps) => (
  <div className="input-group mb-3">
    <span className="input-group-text bg-light text-custom-blue">
      <Icon />
    </span>
    <input
      type={type}
      placeholder={placeholder}
      className="form-control custom-input"
      value={value}
      onChange={onChange}
      aria-label={placeholder}
      autoComplete={type === "password" ? "current-password" : "email"}
    />
  </div>
);

const Login = () => {
  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [modoLogin, setModoLogin] = useState<"INIT" | "LOCAL" | "SSO">("INIT");
  const [ssoConfig, setSsoConfig] = useState<any>(null);

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showBlockedModal, setShowBlockedModal] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const handledRedirectRef = useRef(false);

  const CALLBACK_PATH = PATHS.AUTH_CALLBACK_AZURE_AD;


  useEffect(() => {
    localStorage.removeItem("privacyAccepted");
  }, []);

  const processarRespostaLogin = useCallback(
    async (response: any, emailFinal: string) => {
      const data = response?.data || {};

      const {
        nome,
        idPerfil,
        nomePerfil,
        tipoAutenticacaoUsuario,
        accessToken,
        refreshToken,
        token,
      } = data;

      const finalAccessToken = accessToken || token;
      if (!finalAccessToken) throw new Error("Token não retornado pelo backend");

      saveTokens({
        accessToken: finalAccessToken,
        refreshToken,
      });

      localStorage.setItem(
        "user",
        JSON.stringify({
          nome: nome || emailFinal,
          email: emailFinal,
          idPerfil,
          nomePerfil,
          tipoAutenticacaoUsuario,
        }),
      );

      emitAuthChanged();

      try {
        const perfilResponse = await getPerfil();
        if (perfilResponse.data?.bloqueado) {
          setShowBlockedModal(true);
          return;
        }
        localStorage.setItem("perfil", JSON.stringify(perfilResponse.data));
        emitAuthChanged();
      } catch (err) {
        console.warn("Falha ao buscar perfil:", err);
      }

      navigate(PATHS.HOME);
    },
    [navigate],
  );

  useEffect(() => {
    const run = async () => {
      if (handledRedirectRef.current) return;

      const pending = sessionStorage.getItem(SSO_PENDING_KEY) === "1";
      const isOnCallbackRoute = location.pathname === CALLBACK_PATH;

      if (!pending && !isOnCallbackRoute) return;

      handledRedirectRef.current = true;

      const rawCfg = sessionStorage.getItem(SSO_CFG_KEY);

      let cfg: any = null;
      try {
        cfg = rawCfg ? JSON.parse(rawCfg) : null;
      } catch {
        cfg = null;
      }

      if (!cfg?.authority || !cfg?.clientId) {
        if (isOnCallbackRoute) {
          setError("Configuração SSO ausente. Volte ao login e tente novamente.");
          navigate(PATHS.LOGIN, { replace: true });
        }
        return;
      }

      try {
        setModoLogin("SSO");
        setSsoConfig(cfg);
        setLoading(true);

        const msalInstance = getMsalInstance({
          authority: cfg.authority,
          clientId: cfg.clientId,
        });

        await msalInstance.initialize();

        const result = await msalInstance.handleRedirectPromise();
        if (!result?.account) {
          if (isOnCallbackRoute) navigate(PATHS.LOGIN, { replace: true });
          return;
        }

        const emailFinal =
          sessionStorage.getItem(SSO_EMAIL_KEY) || result.account.username || "";

        const loginResponse = await loginSso({
          email: result.account.username,
          tokenSso: result.idToken,
        });

        sessionStorage.removeItem(SSO_PENDING_KEY);
        sessionStorage.removeItem(SSO_CFG_KEY);
        sessionStorage.removeItem(SSO_EMAIL_KEY);

        await processarRespostaLogin(loginResponse, emailFinal);

        if (isOnCallbackRoute) navigate(PATHS.HOME, { replace: true });
      } catch (err) {
        console.error("Erro ao processar redirect SSO:", err);
        setError("Erro ao processar retorno do login Microsoft. Tente novamente.");
        sessionStorage.removeItem(SSO_PENDING_KEY);
        sessionStorage.removeItem(SSO_CFG_KEY);
        sessionStorage.removeItem(SSO_EMAIL_KEY);

        if (isOnCallbackRoute) navigate(PATHS.LOGIN, { replace: true });
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [processarRespostaLogin, location.pathname, navigate, CALLBACK_PATH]);

  async function executarLoginSsoRedirect(cfg: {
    authority: string;
    clientId: string;
    scopes?: string[];
  }) {
    const { authority, clientId, scopes = ["openid", "profile", "email"] } = cfg;

    const msalInstance = getMsalInstance({ authority, clientId });
    await msalInstance.initialize();

    sessionStorage.setItem(SSO_CFG_KEY, JSON.stringify({ authority, clientId, scopes }));
    sessionStorage.setItem(SSO_EMAIL_KEY, email || "");
    sessionStorage.setItem(SSO_PENDING_KEY, "1");

    await msalInstance.loginRedirect({
      scopes,
      loginHint: email,
      prompt: "select_account",
    });
  }

  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");

    if (!email) {
      setError("Informe o e-mail.");
      return;
    }

    setLoading(true);

    try {
      if (modoLogin === "INIT") {
        const { data } = await loginInit(email);
        const {
          tipo,
          authority,
          clientId,
          scopes = ["openid", "profile", "email"],
        } = data;

        if (tipo === "LOCAL") {
          setModoLogin("LOCAL");
          return;
        }

        if (tipo === "SSO") {
          const cfg = { authority, clientId, scopes };
          setModoLogin("SSO");
          setSsoConfig(cfg);
          await executarLoginSsoRedirect(cfg);
          return;
        }

        throw new Error("Tipo de login inválido");
      }

      if (modoLogin === "LOCAL") {
        if (!senha) {
          setError("Informe a senha.");
          return;
        }

        const loginResponse = await loginLocal({ email, senha });
        await processarRespostaLogin(loginResponse, email);
        return;
      }

      if (modoLogin === "SSO") {
        if (!ssoConfig) throw new Error("Configuração SSO ausente");
        await executarLoginSsoRedirect(ssoConfig);
      }
    } catch (err: any) {
      console.error("Erro login:", err);

      const msg = String(err?.message || "");
      if (msg.includes("hash_empty_error")) {
        setError(
          "Erro ao processar retorno do login Microsoft. Atualize a página e tente novamente.",
        );
      } else if (err?.response?.status === 401 || err?.response?.status === 403) {
        setError("Usuário sem permissão.");
      } else {
        setError("Erro ao fazer login. Procure o DS.");
      }
    } finally {
      setLoading(false);
    }
  };

  const buttonLabel =
    modoLogin === "LOCAL"
      ? "Entrar"
      : modoLogin === "SSO"
        ? "ACESSAR VIA MICROSOFT"
        : "Continuar";

  const hideEmailInput = location.pathname === CALLBACK_PATH;

  return (
    <div className="login-page">
      <section className="login-brand-panel">
        <img
          src="/SistemaFiemg_1.png"
          alt="Sistema Sindical"
          className="login-brand-logo"
        />
      </section>

      <section className="login-form-panel">
        <div
          className="card shadow p-4 rounded login-card"
          style={{ maxWidth: 400, width: "100%" }}
        >
          <img
            src={FiemgLogo}
            alt="FIEMG"
            className="fiemg-logo-azul"
          />

          <form onSubmit={handleLogin}>
            {!hideEmailInput && (
              <InputField
                type="email"
                placeholder="Digite seu e-mail"
                icon={FaEnvelope}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            )}

            {modoLogin === "LOCAL" && (
              <InputField
                type="password"
                placeholder="Digite sua senha"
                icon={FaLock}
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
              />
            )}

            {error && (
              <div className="alert alert-danger mt-2">
                {error}
              </div>
            )}

            <div className="text-end">
              <button
                type="button"
                className="btn btn-link p-0 text-custom-blue"
                onClick={() => setShowForgotPassword(true)}
              >
                Esqueceu sua senha?
              </button>
            </div>

            <button
              type="submit"
              className="btn custom-button w-100 mt-3"
              disabled={loading}
            >
              {loading ? (
                <Spinner
                  animation="border"
                  size="sm"
                  variant="light"
                  role="status"
                  aria-label="Carregando"
                />
              ) : (
                buttonLabel
              )}
            </button>

            <div className="text-center mt-3">
              <a
                href={PRIVACY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-custom-blue privacy-info-link"
              >
                Conheça nosso aviso de privacidade clicando aqui
              </a>
            </div>
          </form>
        </div>

        <ForgotPasswordModal
          show={showForgotPassword}
          handleClose={() => setShowForgotPassword(false)}
        />

        <BlockedUser
          show={showBlockedModal}
          handleClose={() => setShowBlockedModal(false)}
        />
      </section>
    </div>
  );
};

export default Login;